import React, { useEffect, useState } from 'react';
import { useNavigate }                from 'react-router-dom';
import { useAuth }                    from '../contexts/AuthContext';
import { getVehicles }                from '../api/vehicles';
import './DashboardPage.css';

const NAV_ITEMS = [
  { icon: '🚗', label: 'My Projects', key: 'projects',  route: '/projects' },
  { icon: '🔩', label: 'Parts',       key: 'parts'                         },
  { icon: '📷', label: 'Images',      key: 'images'                        },
  { icon: '🔬', label: 'Research',    key: 'research'                      },
  { icon: '🤖', label: 'AI Tools',    key: 'ai'                            },
  { icon: '🏪', label: 'Suppliers',   key: 'suppliers'                     },
  { icon: '📋', label: 'Reports',     key: 'reports'                       },
];

export default function DashboardPage() {
  const { user, signOut }         = useAuth();
  const navigate                  = useNavigate();
  const [vehicle, setVehicle]     = useState(null);
  const [activeNav, setActiveNav] = useState('parts');

  useEffect(() => {
    // 1. Try to use the vehicle that was selected on the Projects page
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) {
      try { setVehicle(JSON.parse(cached)); } catch (_) {}
      return;
    }

    // 2. Fall back: fetch from API and use primary / first vehicle
    getVehicles()
      .then(res => {
        if (res.success && res.data.length) {
          const primary = res.data.find(v => v.is_primary === 1) || res.data[0];
          setVehicle(primary);
          localStorage.setItem('ar_active_vehicle', JSON.stringify(primary));
        } else {
          // No vehicles at all — send them to projects to create one
          navigate('/projects');
        }
      })
      .catch(() => {});
  }, [navigate]);

  function handleSignOut() {
    signOut();
    localStorage.removeItem('ar_active_vehicle');
    navigate('/');
  }

  function handleNavClick(item) {
    if (item.route) {
      navigate(item.route);
    } else {
      setActiveNav(item.key);
    }
  }

  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '—';

  return (
    <div className="dash-root">

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="dash-header">
        <div className="dash-header-brand">
          <span className="dash-header-gear">⚙</span>
          <span className="dash-header-name">AutoRestoration</span>
        </div>

        <div className="dash-header-vehicle">
          {vehicle && (
            <>
              <span className="dash-vehicle-icon">🚗</span>
              <span className="dash-vehicle-label">{vehicleLabel}</span>
              {vehicle.nickname && (
                <span className="dash-vehicle-nick">"{vehicle.nickname}"</span>
              )}
            </>
          )}
        </div>

        <div className="dash-header-user">
          <span className="dash-username">@{user?.username}</span>
          <button className="dash-signout-btn" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </header>

      <div className="dash-body">

        {/* ── Side nav ──────────────────────────────────────────── */}
        <nav className="dash-nav">
          {NAV_ITEMS.map(item => (
            <button
              key={item.key}
              className={`dash-nav-item ${activeNav === item.key ? 'active' : ''} ${item.key === 'projects' ? 'projects-item' : ''}`}
              onClick={() => handleNavClick(item)}
            >
              <span className="dash-nav-icon">{item.icon}</span>
              <span className="dash-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* ── Main content ──────────────────────────────────────── */}
        <main className="dash-main">

          {/* Vehicle hero banner */}
          {vehicle && (
            <div className="dash-vehicle-banner">
              <div className="dash-vehicle-banner-glow" />
              <div className="dash-vehicle-banner-content">
                <p className="dash-vehicle-banner-eyebrow">Active Project</p>
                <h2 className="dash-vehicle-banner-title">{vehicleLabel}</h2>
                {vehicle.nickname && (
                  <p className="dash-vehicle-banner-nick">"{vehicle.nickname}"</p>
                )}
                <div className="dash-vehicle-banner-meta">
                  {vehicle.engine_desc && (
                    <span>🔧 {vehicle.engine_desc}</span>
                  )}
                  {vehicle.transmission_desc && (
                    <span>⚙ {vehicle.transmission_desc}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Welcome card */}
          <div className="dash-welcome">
            <h3 className="dash-welcome-heading">
              Welcome back, {user?.username}.
            </h3>
            <p className="dash-welcome-text">
              Your restoration project is ready. Use the navigation to manage
              parts, upload images, run AI identification, and access research.
            </p>
          </div>

          {/* Quick action tiles — exclude My Projects from the tile grid */}
          <div className="dash-tiles">
            {NAV_ITEMS.filter(i => i.key !== 'projects').map(item => (
              <button
                key={item.key}
                className="dash-tile"
                onClick={() => handleNavClick(item)}
              >
                <span className="dash-tile-icon">{item.icon}</span>
                <span className="dash-tile-label">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Coming soon placeholder for active section */}
          <div className="dash-section-placeholder">
            <p className="dash-section-name">
              {NAV_ITEMS.find(i => i.key === activeNav)?.icon}{' '}
              {NAV_ITEMS.find(i => i.key === activeNav)?.label}
            </p>
            <p className="dash-section-msg">
              This section is coming soon.
            </p>
          </div>

        </main>
      </div>
    </div>
  );
}
